import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import PuttenKaart from '../components/PuttenKaart';
import KaartMetLegenda from '../components/KaartMetLegenda';
import DropZone from '../components/DropZone';
import { nieuweSleutel } from '../components/PuttenPaneel';
import { koppelbaar, blokkadeUitMatches } from '../components/meldingModel';
import { fmtId, leesbareFout } from '../util/format';
import { getPutten, getGebieden, maakPut, puttenUitPdf } from '../api/api';

/* Nieuwe put — zonder houder of inrichting.

   Waarom dit kan: in de database zijn op `putten` alleen x_rd en y_rd
   verplicht. De koppeling met een inrichting woont in de aparte tabel
   inrichting_put, dus een put zonder koppeling is een geldige rij. De
   puttenlijst kent die toestand al en toont hem als "Niet gekoppeld". Er was
   alleen nooit een scherm om er één aan te maken; POST /api/putten en
   maakPut() in api.js stonden al klaar en werden nergens aangeroepen.

   Waarvoor: het register moet putten op de kaart kunnen tonen voordat de
   administratie eromheen rond is. Eerst de put, later de houder.

   Twee wegen ernaartoe, en één scherm erna:

     Handmatig    prikken op de kaart of een RD-coördinaat intypen.
     Uit een PDF  de putten uit de bijlage van een meldingsformulier lezen.

   Allebei komen ze uit bij dezelfde puttenmodule — dezelfde als bij melding
   verwerken en bij een wijzigingsbesluit. Een PDF vult die module alleen
   alvast, hij vervangt hem niet: wat erin staat blijft te bekijken, te
   corrigeren en weg te halen voordat er iets in het register komt.

   Waarom die module en geen eigen venster: PuttenKaart doet dit al, en doet het
   goed — plaatsen op de kaart, coördinaat intypen, perceel erbij zoeken, en de
   kolom ernaast die van lijst naar paneel wisselt. Een tweede, eigen formulier
   ernaast zou hetzelfde werk anders laten voelen zonder dat het iets toevoegt.

   Drie schakelaars staan anders dan bij een melding:

     kwetsbaarBlokkeert   uit. Een melding kan niet in een kwetsbaar gebied,
                          maar registreren wél — het waterschap legt hier vast
                          wat er ís, en dat oordeel is al elders geveld.
     dieptesUitDoel       uit. Er is hier geen beregeningsdoel, dus er is ook
                          geen keurkaartlaag die de maximale diepte bepaalt.
                          De behandelaar vult in wat er in de stukken staat.
     koppelenKan          uit. Koppelen hoort bij een inrichting, en die is er
                          hier niet. De putten uit het register blijven wél op
                          de kaart staan en zijn aan te klikken — dan zie je hun
                          kaartje, alleen zonder knop. Zo weet je waar ze liggen
                          zonder er per ongeluk eentje aan je selectie te
                          hangen die vervolgens nergens heen gaat. */

// Komma als decimaalteken; zo schrijf je meters in het Nederlands.
function getal(waarde) {
  if (waarde === '' || waarde == null) return null;
  const n = Number(String(waarde).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

const rond = (v) => (v == null ? null : Math.round(Number(v)));

/* De putten uit een bijlage → het model van PuttenKaart.

   ── Wanneer staat een put "al in het register"? ────────────────────────────

   Bij een treffer op dezelfde hele meter. Dat is geen oordeel maar een
   gelijkheid: wie in het meldingsformulier een bestaande put opgeeft, kiest die
   uit het register, en dan zijn de opgegeven coördinaten die van de registerput
   zelf. Ligt er een put op drie meter, dan is dat niet dezelfde put maar een
   put in de buurt — en of het er tóch één is, is een vraag voor de behandelaar.

   Vandaar het verschil met puttenUitPreview in meldingModel, dat verder
   hetzelfde doet: daar wordt de vraag niet gesteld en gaan de kandidaten weg.
   Een melding is een eenzijdige rechtshandeling waarvan het waterschap de
   inhoud niet mag wijzigen, dus beslissen dat de melder "eigenlijk" die put op
   drie meter bedoelde is niet aan ons. Hier registreren we alleen wat er in de
   grond zit, en dan is een put dichtbij precies de waarschuwing die je wilt
   zien voordat je er een tweede bovenop zet. De kandidaten blijven dus staan;
   het paneel toont ze onder "Putten dichtbij" en één klik maakt er alsnog
   dezelfde put van.

   Putten zonder coördinaat kunnen niet op de kaart en gaan er niet in. Het
   aantal komt terug zodat het scherm ze kan noemen in plaats van ze te laten
   verdwijnen. */
function puttenUitBijlage(putMatches, registerPutten) {
  const opNummer = new Map((registerPutten || []).map((rp) => [rp.put_id, rp]));
  const bruikbaar = (putMatches || []).filter((pm) => pm.x_rd != null && pm.y_rd != null);

  const putten = bruikbaar.map((pm) => {
    const exact = (pm.matches || []).find(
      (m) => rond(m.x_rd) === rond(pm.x_rd) && rond(m.y_rd) === rond(pm.y_rd)
        && koppelbaar(m)) || null;
    return {
      sleutel: nieuweSleutel(),
      bronId: pm.pdf_id,
      /* Zonder treffer volgt de soort wat de melder zelf opgaf: een te boren
         put blijft een te boren put, ook als wij hem nog niet kennen. */
      soort: exact ? 'gekoppeld' : (pm.bron_volgens_melder === 'nieuw' ? 'nieuw' : 'ongeregistreerd'),
      put_id: exact ? exact.put_id : null,
      // Komt uit het register: de soort ligt vast, daar valt niets aan te kiezen.
      uitRegister: !!exact,
      x_rd: Number(pm.x_rd),
      y_rd: Number(pm.y_rd),
      perceel: pm.perceel || null,
      max_diepte_mv: pm.max_diepte_mv_uit_pdf != null ? String(pm.max_diepte_mv_uit_pdf) : '',
      opgegeven_diepte_m: pm.opgegeven_diepte_m_uit_pdf != null
        ? String(pm.opgegeven_diepte_m_uit_pdf) : '',
      kandidaten: exact ? [] : (pm.matches || []).filter(koppelbaar),
      blokkade: blokkadeUitMatches(pm),
      /* Het besluitnummer komt nooit uit de bijlage — de melder geeft het niet
         op. Bij een registerput weten we het wel, uit het register zelf; dat
         staat er als leestekst bij zodat het paneel volledig is. */
      besluitnummer: (exact && opNummer.get(exact.put_id)?.besluitnummer_wsw) || '',
      herkomst: 'pdf',
    };
  });

  return { putten, overgeslagen: (putMatches || []).length - bruikbaar.length };
}

/* Dezelfde twee kleuren als op de kaart ernaast. Blauw is een put die er al
   stond, oranje eentje die we zojuist hebben aangemaakt — wat het betekent
   staat in de legenda onder de kaart, want oranje betekent elders in deze
   schermen "moet nog geboord worden". */
const STIP_KLEUR = { nieuw: '#ff9500', gekoppeld: '#0071e3' };

function Stip({ soort }) {
  return (
    <span className="pk-stip" style={{
      width: 9, height: 9, background: STIP_KLEUR[soort] || '#86868b',
    }} />
  );
}

/* Kaart links, lijst rechts — dezelfde indeling als de puttenmodule waar je
   net vandaan komt, en als de samenvatting van het meldingsformulier. Je ziet
   in één oogopslag waar de putten liggen en welke nummers ze hebben gekregen,
   in plaats van een kale opsomming waarvan je de ligging zelf moet opzoeken. */
function PuttenOverzicht({ putten, gebieden, aantalNieuw, aantalBekend }) {
  const hoogte = 380;
  const legenda = [
    ...(aantalNieuw ? [{ soort: 'nieuw', label: 'Nieuw aangemaakt', aantal: aantalNieuw }] : []),
    ...(aantalBekend ? [{ soort: 'gekoppeld', label: 'Stond al in het register', aantal: aantalBekend }] : []),
  ];

  return (
    <div className="card mf-kaart">
      <div className="pk-kop">
        <h3 className="pk-titel">Putten</h3>
        <span className="pk-aantal">{putten.length}</span>
      </div>
      <div className="pk-grid">
        <div className="pk-kaartvak">
          <KaartMetLegenda
            punten={putten.map((p) => ({
              put_id: p.put_id, x_rd: p.x_rd, y_rd: p.y_rd,
              perceel: p.perceel, soort: p.soort,
            }))}
            overlays={gebieden}
            hoogte={hoogte}
            fitBijElkeUpdate
            puntLegenda={legenda}
          />
        </div>
        <div className="pk-kolom" style={{ height: hoogte }}>
          <ul className="pk-lijst">
            {putten.map((p) => (
              <li key={p.put_id}>
                <Link className="pk-rij" style={{ textDecoration: 'none' }}
                  to={`/beregening/putten/${p.put_id}`}>
                  <Stip soort={p.soort} />
                  <span className="pk-rij-tekst">
                    <span className="pk-rij-titel mono">{fmtId(p.put_id)}</span>
                    <span className="pk-rij-sub">
                      {Math.round(p.x_rd)}, {Math.round(p.y_rd)}
                      {p.perceel ? ` · ${p.perceel}` : ''}
                    </span>
                  </span>
                  <span className="pk-chevron" aria-hidden="true" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

/* ── Regels voor het schaduwregister ──────────────────────────────────────

   Het schaduwregister houdt buiten dit systeem bij wie de melder is en welk
   recht er loopt; hier staan alleen de putten. De koppeling tussen die twee
   is een regel per put per melding, en die regels zijn precies wat dit scherm
   al weet — op twee dingen na, want een autonummer en een verzoeknummer
   bestaan hier niet.

   Vandaar twee velden, allebei mogen ze leeg blijven: dan zijn de eerste twee
   cellen van elke regel leeg en vul je die kolommen in Excel naar beneden
   door. Wat je niet moet hoeven doen is putnummers en coördinaten overtypen,
   en dat is precies wat hier wordt weggenomen.

   Tabs als scheidingsteken: Excel verdeelt geplakte tabs vanzelf over
   kolommen. Regeleindes worden nieuwe regels. */
function SchaduwRegels({ putten }) {
  const [kenmerk, setKenmerk] = useState('');
  const [gekopieerd, setGekopieerd] = useState(false);

  /* Vier kolommen, beginnend bij Kenmerk. Bewust niet vanaf kolom A: daar
     staat het autonummer, dat dit scherm niet kan weten, en in kolom B staat
     een formule die de bedrijfsnaam opzoekt. Zou je vanaf A plakken, dan
     schreef je die formule weg. */
  const regels = putten.map((p) => [
    kenmerk.trim(),
    fmtId(p.put_id),
    Math.round(p.x_rd),
    Math.round(p.y_rd),
  ].join('\t')).join('\n');

  const kopieer = async () => {
    try {
      await navigator.clipboard.writeText(regels);
    } catch {
      /* Geen toestemming of een oudere browser: dan het oude trucje met een
         onzichtbaar veld. Lukt ook dat niet, dan staan de regels hieronder
         gewoon te lezen en kun je ze zelf selecteren. */
      const veld = document.createElement('textarea');
      veld.value = regels;
      veld.style.position = 'fixed';
      veld.style.opacity = '0';
      document.body.appendChild(veld);
      veld.select();
      try { document.execCommand('copy'); } catch { /* dan handmatig */ }
      document.body.removeChild(veld);
    }
    setGekopieerd(true);
    setTimeout(() => setGekopieerd(false), 2500);
  };

  const veldStijl = {
    font: 'inherit', fontSize: 14, padding: '7px 10px',
    border: '1px solid var(--border)', borderRadius: 8, width: 180,
  };

  return (
    <>
      <h2 style={{ margin: '28px 0 8px', fontSize: 15, fontWeight: 600, letterSpacing: '-0.01em' }}>
        Regels voor je schaduwregister
      </h2>
      <p style={{ margin: '0 0 12px', fontSize: 13.5, color: 'var(--text-soft)', lineHeight: 1.55 }}>
        {putten.length === 1 ? 'Eén regel' : `${putten.length} regels`} voor blad Koppelingen —
        alle putten van deze melding, ook die al in het register stonden. Verwijderen hoeft
        er nooit iets.
      </p>

      <div className="card">
        <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={{ fontSize: 13, color: 'var(--text-soft)' }}>
              Kenmerk (verzoek- of zaaknummer)
            </span>
            <input style={{ ...veldStijl, width: 220 }} value={kenmerk}
              placeholder="bijv. 2026042100123"
              onChange={(e) => setKenmerk(e.target.value)} />
          </label>
          <button className="btn btn-secondary" onClick={kopieer}>
            {gekopieerd ? 'Gekopieerd' : 'Kopieer de regels'}
          </button>
        </div>

        <p style={{ margin: '14px 0 6px', fontSize: 13, color: 'var(--text-muted)' }}>
          Plak ze in de eerste lege regel van <strong>kolom C</strong> — dat is de kolom
          Kenmerk. Vul daarna kolom A met het autonummer; kolom B vult zichzelf.
          Het kenmerk mag hier ook leeg blijven, dan vul je die kolom in Excel door.
        </p>

        {/* Ook zichtbaar, niet alleen kopieerbaar: gaat het klembord om welke
            reden dan ook niet mee, dan kun je hier gewoon selecteren. En je
            ziet wat je plakt voordat je het plakt. */}
        <pre style={{
          margin: 0, padding: '10px 12px', overflowX: 'auto',
          background: 'var(--surface-3)', borderRadius: 8,
          fontSize: 12.5, lineHeight: 1.7, whiteSpace: 'pre',
        }}>{regels.replace(/\t/g, '    ')}</pre>
      </div>
    </>
  );
}

export default function PutNieuw() {
  const navigate = useNavigate();
  /* 'keuze'  → handmatig of uit een PDF
     'pdf'    → de bijlage kiezen
     'kaart'  → de puttenmodule, waar beide wegen op uitkomen */
  const [stap, setStap] = useState('keuze');
  const [putten, setPutten] = useState([]);
  const [registerPutten, setRegisterPutten] = useState([]);
  const [gebieden, setGebieden] = useState([]);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState(null);
  // Wat er uit de bijlage kwam: { bestand, tekstlaag, gelezen, overgeslagen }.
  const [bijlage, setBijlage] = useState(null);
  /* Het resultaat na opslaan: { gemaakt, bestaand }. Zolang dit null is staat
     de kaart in beeld, daarna de bevestiging. Geen aparte route: de putnummers
     komen pas van de server terug, en die in een URL proppen om ze daarna weer
     op te halen is omslag zonder winst. */
  const [gereed, setGereed] = useState(null);

  useEffect(() => {
    /* De bestaande putten staan hier niet om te koppelen — daar is geen
       inrichting voor — maar om te zíen. Zonder die laag prik je zo een
       tweede put bovenop een put die er al staat. */
    getPutten()
      .then((r) => setRegisterPutten((r.data || []).filter((p) => !p.buiten_gebruik_datum)))
      .catch(() => {});
    // Eigen cache van 5 minuten in de api-laag, dus doorgaans geen netwerkcall.
    getGebieden(undefined, true).then((r) => setGebieden(r.data || [])).catch(() => {});
  }, []);

  /* Wat er aangemaakt moet worden, en wat er al is. Een put met een putnummer
     staat in het register; die maken we niet nog eens. Handmatig kan hij niet
     in de lijst belanden — koppelenKan staat uit — maar uit een bijlage wel,
     en dat is precies het onderscheid dat dit scherm moet maken. */
  const nieuwe = putten.filter((p) => !p.put_id);
  const bestaand = putten.filter((p) => p.put_id);

  const terug = useCallback(() => navigate('/beregening/putten'), [navigate]);

  const leesPdf = async (file) => {
    if (!file.name.toLowerCase().endsWith('.pdf')) { setFout('Alleen PDF-bestanden.'); return; }
    setFout(null);
    setBezig(true);
    try {
      const formData = new FormData();
      formData.append('pdf', file);
      const res = await puttenUitPdf(formData);
      const { putten: uitPdf, overgeslagen } = puttenUitBijlage(res.data.put_matches, registerPutten);
      if (!uitPdf.length) {
        setFout('Geen van de putten in deze bijlage heeft een coördinaat, '
          + 'dus er valt niets op de kaart te zetten.');
        return;
      }
      setPutten(uitPdf);
      setBijlage({
        bestand: file.name,
        tekstlaag: res.data.bron === 'tekstlaag',
        gelezen: uitPdf.length,
        overgeslagen,
      });
      setStap('kaart');
    } catch (e) {
      setFout(leesbareFout(e));
    } finally {
      setBezig(false);
    }
  };

  const opslaan = async () => {
    if (!nieuwe.length) {
      setFout('Plaats eerst een put op de kaart.');
      return;
    }
    setBezig(true);
    setFout(null);

    /* Eén voor één, niet allemaal tegelijk. Het gaat om een handvol putten, en
       bij een fout halverwege weet je zo precies welke er wél in staan. */
    const gemaakt = [];
    try {
      for (const p of nieuwe) {
        const body = { x_rd: Number(p.x_rd), y_rd: Number(p.y_rd) };
        if (p.perceel) body.perceel = p.perceel;
        const max = getal(p.max_diepte_mv);
        if (max != null) body.max_diepte_mv = max;
        const werkelijk = getal(p.opgegeven_diepte_m);
        if (werkelijk != null) body.opgegeven_diepte_m = werkelijk;
        if (p.besluitnummer) body.besluitnummer_wsw = p.besluitnummer;

        const res = await maakPut(body);
        gemaakt.push(res.data);
      }
    } catch (e) {
      setBezig(false);
      setFout(
        (gemaakt.length ? `${gemaakt.length} van de ${nieuwe.length} putten zijn opgeslagen. ` : '')
        + leesbareFout(e)
      );
      // Wat al gelukt is niet nog eens aanmaken bij een tweede poging.
      setPutten((huidig) => huidig.filter((p) => !gemaakt.some(
        (g) => Number(g.x_rd) === Number(p.x_rd) && Number(g.y_rd) === Number(p.y_rd))));
      return;
    }

    setBezig(false);
    /* De bestaande putten gaan mee de bevestiging in: uit een bijlage van vijf
       putten waarvan er twee al bekend waren wil je alle vijf de nummers zien,
       anders blijf je zoeken naar de twee die er niet bij staan. */
    setGereed({ gemaakt, bestaand });
    setPutten([]);
  };

  const opnieuw = () => {
    setGereed(null);
    setBijlage(null);
    setFout(null);
    setStap('keuze');
  };

  const kop = (titel, onder) => (
    <div className="page-header">
      <div>
        <h1 className="page-title">{titel}</h1>
        <p className="page-subtitle">{onder}</p>
      </div>
      {/* De weg waarlangs de putten binnenkomen bepaalt wat er in beeld staat,
          dus die hoort in de paginakop en niet ergens halverwege. */}
      {bijlage && !gereed && <span className="mf-badge is-bijlage">Uit bijlage</span>}
    </div>
  );

  // ── Klaar ───────────────────────────────────────────────────────────────
  if (gereed) {
    const { gemaakt, bestaand: albekend } = gereed;
    const meer = gemaakt.length !== 1;
    /* Eén lijst, twee kleuren. De putten die er al stonden horen er even goed
       bij als de nieuwe: samen zijn ze de putten van deze melding, en dat is
       wat je hier wilt overzien. Welke welke is zegt de legenda. */
    const alle = [
      ...gemaakt.map((p) => ({ ...p, soort: 'nieuw' })),
      ...albekend.map((p) => ({ ...p, soort: 'gekoppeld' })),
    ];
    return (
      <div className="main-inner main-inner-wide">
        {kop(
          meer ? `${gemaakt.length} putten toegevoegd` : 'Put toegevoegd',
          `${meer ? 'Ze staan' : 'Hij staat'} in het register met de status Niet gekoppeld.`
          + (albekend.length
            ? ` ${albekend.length === 1
              ? 'Eén put uit de bijlage lag op de plek van een put die we al kennen en is niet opnieuw aangemaakt.'
              : `${albekend.length} putten uit de bijlage lagen op de plek van putten die we al kennen en zijn niet opnieuw aangemaakt.`}`
            : ''),
        )}

        <PuttenOverzicht putten={alle} gebieden={gebieden}
          aantalNieuw={gemaakt.length} aantalBekend={albekend.length} />

        {/* Beide sets bij elkaar: voor het schaduwregister is een put die al
            bestond net zo goed een koppeling van deze melding als een put die
            we net hebben aangemaakt. */}
        <SchaduwRegels putten={alle} />

        <footer className="mf-voet">
          <button className="btn btn-ghost" onClick={opnieuw}>Nog putten toevoegen</button>
          <button className="btn btn-primary" onClick={() => navigate('/beregening/putten')}>
            Naar de puttenlijst
          </button>
        </footer>
      </div>
    );
  }

  // ── Waarlangs? ──────────────────────────────────────────────────────────
  if (stap === 'keuze') {
    return (
      <div className="main-inner">
        {kop('Nieuwe put', 'Zonder houder of inrichting — de put krijgt de status Niet gekoppeld.')}

        <div className="card">
          <p className="mf-start-vraag">Waar komen de putten vandaan?</p>
          <div className="mf-start-keuzes">
            <button className="mf-start-kaart" onClick={() => { setFout(null); setStap('kaart'); }}>
              <span className="mf-start-titel">Handmatig</span>
              <span className="mf-start-sub">
                Prik ze aan op de kaart, of voer een RD-coördinaat of perceel in.
              </span>
            </button>
            <button className="mf-start-kaart" onClick={() => { setFout(null); setStap('pdf'); }}>
              <span className="mf-start-titel">Uit een meldingsformulier</span>
              <span className="mf-start-sub">
                De putten uit de bijlage worden ingelezen. Putten die al in het
                register staan herkennen we aan hun coördinaat.
              </span>
            </button>
          </div>
        </div>

        <footer className="mf-voet">
          <button className="btn btn-ghost" onClick={terug}>Annuleren</button>
        </footer>
      </div>
    );
  }

  // ── De bijlage ──────────────────────────────────────────────────────────
  if (stap === 'pdf') {
    return (
      <div className="main-inner">
        {kop('Nieuwe put', 'Uit de bijlage van een meldingsformulier.')}

        {fout && <div className="alert alert-error" style={{ marginBottom: 16 }}>{fout}</div>}

        {bezig ? (
          <div className="empty-state"><span className="spinner" /> Bijlage wordt gelezen…</div>
        ) : (
          <DropZone
            accept="application/pdf"
            onFile={leesPdf}
            label="Sleep de bijlage van het meldingsformulier hierheen"
            hint="of klik om een bestand te kiezen"
          />
        )}

        <footer className="mf-voet">
          <button className="btn btn-ghost" onClick={() => { setFout(null); setStap('keuze'); }}>
            Terug
          </button>
        </footer>
      </div>
    );
  }

  // ── De puttenmodule ─────────────────────────────────────────────────────
  const knopTekst = bezig
    ? 'Bezig…'
    : nieuwe.length > 1 ? `${nieuwe.length} putten toevoegen` : 'Put toevoegen';

  const allesAlBekend = bijlage && nieuwe.length === 0 && bestaand.length > 0;

  return (
    <div className="main-inner">
      {kop(
        'Nieuwe put',
        'Zonder houder of inrichting — de putten krijgen de status Niet gekoppeld.',
      )}

      {bijlage && (
        <div className="card" style={{ marginBottom: 16, padding: '14px 18px' }}>
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6 }}>
            Uit <strong>{bijlage.bestand}</strong>: <strong>{bijlage.gelezen}</strong>{' '}
            {bijlage.gelezen === 1 ? 'put' : 'putten'}.{' '}
            {bestaand.length === 0
              ? 'Geen daarvan staat al in het register.'
              : (
                <>
                  {bestaand.length === 1
                    ? 'Eén daarvan staat al in het register'
                    : `Daarvan staan er ${bestaand.length} al in het register`}
                  {' '}({bestaand.map((p) => fmtId(p.put_id)).join(', ')}) en{' '}
                  {bestaand.length === 1 ? 'wordt' : 'worden'} niet opnieuw aangemaakt.
                </>
              )}
          </p>
          {bijlage.overgeslagen > 0 && (
            <p style={{ margin: '8px 0 0', fontSize: 13.5, color: 'var(--text-soft)', lineHeight: 1.55 }}>
              {bijlage.overgeslagen === 1
                ? 'Eén put in de bijlage heeft geen coördinaat en staat er niet bij.'
                : `${bijlage.overgeslagen} putten in de bijlage hebben geen coördinaat en staan er niet bij.`}
            </p>
          )}
          {/* Geen bijlage betekent dat de PDF opnieuw is opgeslagen; dan komen de
              gegevens uit de tekst op het papier en niet uit het formulier zelf.
              Precies waar het meldingsformulier voor waarschuwt, dus staat het
              hier ook. */}
          {bijlage.tekstlaag && (
            <p style={{ margin: '8px 0 0', fontSize: 13.5, color: 'var(--text-soft)', lineHeight: 1.55 }}>
              De machineleesbare bijlage ontbreekt in deze PDF; de gegevens zijn
              uit de tekst gelezen. Controleer ze extra goed.
            </p>
          )}
        </div>
      )}

      <div className="card mf-kaart">
        <PuttenKaart
          putten={putten}
          onChange={setPutten}
          registerPutten={registerPutten}
          gebieden={gebieden}
          hoogte={520}
          kwetsbaarBlokkeert={false}
          dieptesUitDoel={false}
          koppelenKan={false}
        />
      </div>

      {fout && <div className="alert alert-error" style={{ marginTop: 4 }}>{fout}</div>}

      {/* Een bijlage waarin álle putten al bekend zijn: er valt niets toe te
          voegen, en een uitgeschakelde knop zonder uitleg leest als een storing. */}
      {allesAlBekend && !fout && (
        <div className="alert alert-info" style={{ marginTop: 4 }}>
          Alle putten uit deze bijlage staan al in het register. Er is niets toe te voegen.
        </div>
      )}

      <footer className="mf-voet">
        <button className="btn btn-ghost" onClick={terug} disabled={bezig}>Annuleren</button>
        <button className="btn btn-primary" onClick={opslaan} disabled={bezig || !nieuwe.length}>
          {knopTekst}
        </button>
      </footer>
    </div>
  );
}
